import dayjs from 'dayjs';
import { enqueueSnackbar } from 'notistack';
import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router';
import { useSearchParams } from 'react-router-dom';
import RecommendationCreate from '/@/components/recommendation-create';
// start steps components
import ApplicationMomentStep from '/@/components/recommendation-create/crop-protection/steps/application-moment';
import CropProtectionTypeStep from '/@/components/recommendation-create/crop-protection/steps/crop-protection-type';
import CropStep from '/@/components/recommendation-create/crop-protection/steps/crop-step';
import CropProtectionFieldStep from '/@/components/recommendation-create/crop-protection/steps/field-step';
import LoadCPCreation from '/@/components/recommendation-create/crop-protection/steps/load-cp-creation-step';
import SpecificationsStep from '/@/components/recommendation-create/crop-protection/steps/specifications';
// end steps components
import { useCropProtectionOptions } from '/@/hooks/crop-protection';
import { useFieldById } from '/@/hooks/fields';
import { useRecommendationById } from '/@/hooks/my-recommendations/useFilterMyRecommendations';
import { useIsValidatedBayerUserType } from '/@/hooks/session';
import { _ } from '/@/locale';
import { PLAGUE_CROPS, WEED_APPLICATION_MOMENT_CROPS } from '/@/store/crop-protection/selectors';
import {
    createRecommendationFromAPI,
    updateRecommendationFromAPI,
} from '/@/store/private-data/recommendations/apiProviders';
import { getAppInstance } from '/@/store/session/selectors';
import { mpTrackEvent } from '/@/store/tracking/mixpanel-events';
import type { CropProtectionForm } from '/@/types/crop-protection';
import type { Field } from '/@/types/field';
import type { Step } from '/@/types/recommendation-create';
import { generateUniques } from '/@/utils/crop-protection';
import useWindowSize from '/@/utils/hooks/window-size';
import { reverse } from '/@/utils/router';

function CropProtectionRecommendationCreate({ recommendationId }: { recommendationId: number }) {
    const [activeStep, setActiveStep] = useState<number>(0);
    const [field, setField] = useState<Field | undefined>(undefined);
    const form = useForm<CropProtectionForm>({
        defaultValues: {
            fieldId: 0,
            crop: '',
            plantingDate: '',
            type: '',
            application: '',
            weedType: '',
            weed: '',
            weedSize: '',
            recommendationOption: '',
            diseaseId: '',
            applicationType: '',
            plagueId: '',
        },
    });

    const isMobile = useWindowSize().width <= 900;

    // query params for form autocomplete
    const { search } = useLocation();
    const query = new URLSearchParams(search);
    const fieldId = Number(query.get('fieldId'));
    const cropQuery = query.get('crop');
    const typeQuery = query.get('type');
    const plantingDateQuery = query.get('plantingDate');
    const applicationTypeQuery = query.get('applicationType');
    const { data: fieldData, isLoading: isFieldDataLoading } = useFieldById({ id: fieldId }, { enabled: !!fieldId }); //this is for queryParam
    const { data: fieldDetail } = useFieldById({ id: field?.id }, { enabled: !!field?.id }); // this is for CropStep that needs the full data of the selected field

    useEffect(() => {
        const formDefaultValues: Partial<CropProtectionForm> = {};
        let counter = 0; // for redirecting to following steps if data is provided

        if (fieldId && fieldData) {
            setField(fieldData);
            formDefaultValues.fieldId = fieldData.id;
            counter++;
        }
        if (cropQuery) {
            formDefaultValues.crop = cropQuery;
        }
        if (typeQuery) {
            formDefaultValues.type = typeQuery;
        }
        if (plantingDateQuery) {
            formDefaultValues.plantingDate = dayjs(plantingDateQuery);
        }
        if (applicationTypeQuery) {
            formDefaultValues.applicationType = applicationTypeQuery;
        }
        if (counter && cropQuery && plantingDateQuery) counter++; // only increment if fieldId is set correctly and crop and planting date are set

        form.reset(formDefaultValues);
        setActiveStep(counter);
    }, [fieldId, applicationTypeQuery, cropQuery, plantingDateQuery, typeQuery, fieldData, form]);

    // end query params processing

    // CP options for defining when CP reco is finished
    const filteredWeedOptionsQuery = useCropProtectionOptions({
        type: form.getValues().type,
        crop: form.getValues().crop,
        weed: form.getValues().weed,
        applicationMoment: form.getValues().application,
        weedType: form.getValues().weedType,
        weedSize: form.getValues().weedSize,
        applicationType: form.getValues().applicationType,
        diseaseId: form.getValues().diseaseId,
        plagueId: form.getValues().plagueId,
    });
    const isFinalRecommendationOption = filteredWeedOptionsQuery?.data?.length === 1 && activeStep !== 0;

    useEffect(() => {
        if (isFinalRecommendationOption) {
            form.setValue('recommendationOption', filteredWeedOptionsQuery.data?.[0]?.id ?? '');
        }
    }, [isFinalRecommendationOption, filteredWeedOptionsQuery.data, form]);

    // end CP options processing

    const handleClickNext = async () => {
        if (!isFinalRecommendationOption) {
            setActiveStep((prevStep) => prevStep + 1);
            return;
        }

        if (recommendationId) {
            updateRecommendation();
        } else {
            mpTrackEvent('Crop Protection Recommendation created', {
                crop: form.getValues().crop,
                cp_type: form.getValues().type.substring(0, form.getValues().type.length - 1),
            });
            createRecommendation();
        }
        setActiveStep(steps.length - 1);
    };

    const specificationsStepTitleMapping: Record<string, string> = useMemo(() => {
        return {
            weeds: _('Tipo de maleza'),
            plagues: _('Tipo de plaga'),
            diseases: _('Tipo de enfermedad'),
            seed_treatment: _('Tipo de maleza'),
        };
    }, []);

    const specificationsStepSubtitleMapping: Record<string, string> = useMemo(() => {
        return {
            weeds: _('Indicá la familia, el nombre y el tamaño de la maleza que deseas controlar.'),
            plagues: _('Indicá el nombre de la plaga que deseas controlar.'),
            diseases: _('Indicá el nombre de la enfermedad que deseas tratar.'),
            seed_treatment: _('Indicá la familia, el nombre y el tamaño de la maleza que deseas controlar.'),
        };
    }, []);

    const onClickNextText = useMemo(() => {
        return isFinalRecommendationOption
            ? recommendationId
                ? isMobile
                    ? _('Actualizar')
                    : _('Actualizar recomendación')
                : isMobile
                  ? _('Crear')
                  : _('Crear recomendación')
            : undefined;
    }, [isFinalRecommendationOption, recommendationId, isMobile]);

    const navigate = useNavigate();
    const appInstance = useSelector(getAppInstance);
    const recommendationQuery = useRecommendationById(recommendationId);

    const generateRecommendationData = (isUpdate: boolean) => {
        const baseData = {
            plantingDate: form.getValues().plantingDate
                ? form.getValues().plantingDate?.$d.toISOString().split('T')[0]
                : null,
            result_id: form.getValues().recommendationOption,
        };

        const data = {
            weed_v2: form.getValues().type === 'weeds' ? { ...baseData } : {},
            disease: form.getValues().type === 'diseases' ? { ...baseData } : {},
            plague: form.getValues().type === 'plagues' ? { ...baseData } : {},
            seed_treatment: form.getValues().type === 'seed_treatment' ? { ...baseData } : {},
            backup: {
                status: 'draft',
                crops: [],
                weeds: [],
                weed_v2: form.getValues().type === 'weeds' ? { ...baseData } : {},
                disease: form.getValues().type === 'diseases' ? { ...baseData } : {},
                plague: form.getValues().type === 'plagues' ? { ...baseData } : {},
                seed_treatment: form.getValues().type === 'seed_treatment' ? { ...baseData } : {},
                field,
                brand: undefined,
            },
        };

        if (isUpdate && recommendationQuery.data) {
            data.weed_v2 =
                form.getValues().type === 'weeds' ? { ...baseData, id: recommendationQuery.data.weedV2?.id } : {};
            data.disease =
                form.getValues().type === 'diseases'
                    ? { ...baseData, id: recommendationQuery.data.diseaseRecommendation?.id }
                    : {};
            data.plague =
                form.getValues().type === 'plagues'
                    ? { ...baseData, id: recommendationQuery.data.plagueRecommendation?.id }
                    : {};
            data.seed_treatment =
                form.getValues().type === 'seed_treatment'
                    ? { ...baseData, id: recommendationQuery.data.seedTreatmentRecommendation?.id }
                    : {};
            data.backup = {
                ...recommendationQuery.data,
                field:
                    field ??
                    (typeof recommendationQuery.data.backup?.field === 'object'
                        ? recommendationQuery.data.backup.field
                        : undefined),
                weed_v2:
                    form.getValues().type === 'weeds' ? { ...baseData, id: recommendationQuery.data.weedV2?.id } : {},
                disease:
                    form.getValues().type === 'diseases'
                        ? { ...baseData, id: recommendationQuery.data.diseaseRecommendation?.id }
                        : {},
                plague:
                    form.getValues().type === 'plagues'
                        ? { ...baseData, id: recommendationQuery.data.plagueRecommendation?.id }
                        : {},
                seed_treatment:
                    form.getValues().type === 'seed_treatment'
                        ? { ...baseData, id: recommendationQuery.data.seedTreatmentRecommendation?.id }
                        : {},
            };
        }

        return data;
    };

    const createRecommendation = async () => {
        try {
            const newReco = await createRecommendationFromAPI({
                appInstance: appInstance.id,
                crops: [],
                field: field.id,
                status: 'saved',
                ...generateRecommendationData(false),
            });
            navigate(
                reverse('cropProtectionRecommendationDetail', {
                    params: { recommendationId: newReco.id },
                })
            );
            const cp_type = newReco.weedV2
                ? 'weed'
                : newReco.diseaseRecommendation
                  ? 'disease'
                  : newReco.seedTreatmentRecommendation
                    ? 'seed_treatment'
                    : 'plague';
            const crop = (
                newReco.weedV2 ||
                newReco.diseaseRecommendation ||
                newReco.plagueRecommendation ||
                newReco.seedTreatmentRecommendation
            )?.result?.crop;
            mpTrackEvent('Crop Protection Recommendation saved', {
                entry_source: 'crop-protection-recommendation-detail',
                crop: crop,
                cp_type: cp_type,
            });
        } catch (error) {
            enqueueSnackbar(_('Ha ocurrido un error, por favor inténtalo más tarde.'), {
                variant: 'error',
            });
        }
    };

    const updateRecommendation = async () => {
        try {
            const newReco = await updateRecommendationFromAPI({
                ...recommendationQuery.data,
                ...generateRecommendationData(true),
            });
            return navigate(
                reverse('cropProtectionRecommendationDetail', {
                    params: { recommendationId: newReco.id },
                })
            );
        } catch (error) {
            enqueueSnackbar(_('Ha ocurrido un error, por favor inténtalo más tarde.'), {
                variant: 'error',
            });
        }
    };

    const [searchParams] = useSearchParams();
    const userNodeId = searchParams.get('userNodeId') ? Number(searchParams.get('userNodeId')) : undefined;
    const isBayer = useIsValidatedBayerUserType();
    const hasGrower = !userNodeId && isBayer;

    const crop = form.getValues().crop;
    const protectionType = form.getValues().type;
    const showApplicationMoment =
        (protectionType === 'weeds' &&
            WEED_APPLICATION_MOMENT_CROPS.includes(crop as (typeof WEED_APPLICATION_MOMENT_CROPS)[number])) ||
        // seed_treatment: no application-moment step — product is selected by crop only
        (protectionType === 'plagues' && PLAGUE_CROPS.includes(crop as (typeof PLAGUE_CROPS)[number]));

    const steps: Step[] = [
        {
            sidebarName: _('Selección de lote'),
            recommendationType: _('Protección de Cultivos'),
            title: _('Elegí el lote para generar la recomendación'),
            subtitle: _(
                'Seleccioná {{fieldText}} para obtener una recomendación ajustada a sus condiciones específicas.',
                { fieldText: hasGrower ? _('el lote de tu productor') : _('el lote') }
            ),
            content: (
                <CropProtectionFieldStep
                    field={field}
                    setField={(field) => {
                        form.setValue('fieldId', field.id);
                        setField(field);
                    }}
                    form={form}
                />
            ),
            onClickNext: handleClickNext,
            onClickNextDisabled: () => {
                if (recommendationId && recommendationQuery.isValidating) return true;
                return form.getValues().fieldId === 0;
            },
            onClickBackDisabled: () => true,
        },
        {
            sidebarName: _('Cultivo'),
            recommendationType: _('Protección de Cultivos'),
            title: _('Protección de Cultivos'),
            subtitle: _(
                'Descubrí todo nuestro portfolio de soluciones para proteger tu cultivo de malezas, enfermedades y plagas.'
            ),
            content: <CropStep field={fieldDetail} form={form} />,
            onClickNext: handleClickNext,
            onClickNextText: onClickNextText,
            onClickNextDisabled: () => {
                if (recommendationId && recommendationQuery.isValidating) return true;
                return form.getValues().crop === '' || form.getValues().crop === undefined;
            },
            onClickBackDisabled: () => false,
        },
        {
            sidebarName: _('Tipo de protección'),
            recommendationType: _('Protección de Cultivos'),
            title: _('Tipo de protección'),
            subtitle: _(
                'Elige entre malezas, plagas o enfermedades y encuentra la solución ideal para proteger tus cultivos.'
            ),
            content: <CropProtectionTypeStep defaultType={form.getValues()?.type} form={form} />,
            onClickNext: handleClickNext,
            onClickNextText: onClickNextText,
            onClickNextDisabled: () => {
                if (recommendationId && recommendationQuery.isValidating) return true;
                return form.getValues().type === '' || form.getValues().type === undefined;
            },
        },
        ...(showApplicationMoment
            ? [
                  {
                      sidebarName: _('Momento de Aplicación'),
                      recommendationType: _('Protección de Cultivos'),
                      title: _('Momento de Aplicación'),
                      subtitle:
                          protectionType === 'plagues'
                              ? _(
                                    'Seleccioná cuándo aplicarás el insecticida, durante la pre emergencia del cultivo o en el periodo vegetativo.'
                                )
                              : protectionType === 'seed_treatment'
                                ? _(
                                      'Seleccioná cuándo aplicarás el tratamiento de semillas: antes de la siembra, durante la emergencia o en pleno crecimiento del cultivo.'
                                  )
                                : _(
                                      'Seleccioná cuándo aplicarás el herbicida: antes de la siembra, durante la emergencia o en pleno crecimiento del cultivo.'
                                  ),
                      content: <ApplicationMomentStep form={form} />,
                      onClickNext: handleClickNext,
                      onClickNextText: onClickNextText,
                      onClickNextDisabled: () => {
                          if (recommendationId && recommendationQuery.isValidating) return true;
                          return form.getValues().application === '' || form.getValues().application === undefined;
                      },
                  },
              ]
            : []),
        {
            sidebarName: _('Especificaciones'),
            recommendationType: _('Protección de Cultivos'),
            title: specificationsStepTitleMapping[form.getValues().type],
            subtitle: specificationsStepSubtitleMapping[form.getValues().type],
            content: <SpecificationsStep form={form} />,
            onClickNext: handleClickNext,
            onClickNextText: onClickNextText,
            onClickNextDisabled: () => {
                if (!isFinalRecommendationOption) return true;
                if (recommendationId && recommendationQuery.isValidating) return true;
                // seed_treatment: gated only on isFinalRecommendationOption (crop-only selection, no weed fields)
                if (form.getValues().type === 'seed_treatment') return false;
                if (form.getValues().type === 'weeds') {
                    const weedOptions = generateUniques('weed', filteredWeedOptionsQuery.data);
                    if (weedOptions.length > 0) {
                        const weedSizeOptions = generateUniques('weedSize', filteredWeedOptionsQuery.data);
                        if (weedSizeOptions.length > 0) {
                            return !form.getValues().weedType && !form.getValues().weed && !form.getValues().weedSize;
                        }
                        return !form.getValues().weedType && !form.getValues().weed;
                    }
                    return !form.getValues().weedType;
                }
                if (form.getValues().type === 'plagues') {
                    return !form.getValues().plagueId;
                }
                if (form.getValues().type === 'diseases') {
                    if (form.getValues().crop === 'soy') {
                        return !form.getValues().diseaseId && form.getValues().applicationType === '';
                    }
                    return !form.getValues().diseaseId;
                }
                return false;
            },
        },
        {
            sidebarName: _('Recomendación'),
            content: <LoadCPCreation form={form} />,
            onClickNextDisabled: () => true,
            onClickBackDisabled: () => true,
        },
    ];

    return RecommendationCreate({
        steps,
        activeStep,
        setActiveStep,
        isLoading: isFieldDataLoading,
        areActionsLoading: filteredWeedOptionsQuery.isLoading,
    });
}

export default CropProtectionRecommendationCreate;
